import { reaction } from 'mobx';
import * as services from '@api';
import { requiredValidate } from '../utils';
import { isType } from '@src/utils';
import { PAGE_INDEX, ROW_KEY } from '@stores/cardstore/constants';

/**
 * detail
 * @param rootStore
 * @param options {
 *  // 必填字段
 *  cardStoreKey: string，默认值：cardStore
 *  // 选填字段
 *  dataSourceAlias: string， 默认值：cardStore.dataSource
 *  billNo: string， 默认值：params.billNo
 *  billId: string， 默认值：params.billId
 *  writeBack: boolean, 默认值：true
 * }
 * @returns
 * @hooks {
 *  // 专有钩子
 *  beforeDetailHook: 详情请求前钩子
 *  afterDetailHook: 详情请求后钩子
 *  // 公共钩子
 *  beforeHook: 开始钩子
 *  afterHook: 结束钩子
 *  catchHook: 异常钩子
 *  finallyHook: 最终钩子
 * }
 */
export async function detail (rootStore: any, options: any = {}) {
  requiredValidate.call(this, options, ['cardStoreKey']);
  const { dataSourceAlias = '', cardStoreKey = 'cardStore', writeBack = true } = options;
  const { pageStore = {} } = rootStore;
  const { params = {}, query = {} } = pageStore;
  const cardStore = rootStore.get(cardStoreKey);
  if (!cardStore) throw (new Error(`The CardStore[${cardStoreKey}] not found`));

  try {
    this?.showProgress?.()
    const queryData = cardStore.getQueryParams({ alias: cardStoreKey })?.data
    let detailParams = {
      params: {
        ...query,
        tplid: params.tplId,
        mode: cardStore.uiState || pageStore.status,
        datasource: dataSourceAlias || cardStore?.dataSource || '',
        billnum: options.billNo || params.billNo,
        busiObj: options.busiObj || query.busiObj,
        id: options.billId || params.billId
      },
      data: queryData
    };
    // 钩子事件
    if (isType(this.beforeDetailHook, 'Function')) {
      detailParams = await this.beforeDetailHook(rootStore, detailParams);
    }
    let detailRes = await services.detail(detailParams);
    if (isType(this.afterDetailHook, 'Function')) {
      detailRes = await this.afterDetailHook(rootStore, detailRes);
    }

    // 纠正billId、code
    const detailResData = detailRes.data;
    Object.assign(params, { billId: detailResData.id, code: detailResData.code });

    // 数据回写
    const detailData = detailRes?.data
    cardStore.tableStores?.forEach(sDescription => {
      const { paging, dataSource: sDataSource, children } = sDescription
      const pagingChildren = children?.filter(child => !!child.paging)
      detailData[sDataSource]?.forEach(sRow => {
        if (paging) {
          sRow[PAGE_INDEX] = 1
        }
        if (pagingChildren?.length) {
          pagingChildren.forEach(gDescription => {
            const { dataSource: gDataSource, alias: gAlias } = gDescription
            sRow[gDataSource]?.forEach(gRow => {
              gRow[PAGE_INDEX] = 1
            })
            const gStore = rootStore[gAlias]
            if (!gStore.loadedPages) {
              gStore.loadedPages = {}
            }
            if (!gStore.loadedPages[sRow.id]) {
              gStore.loadedPages[sRow.id] = []
            }
            gStore.loadedPages[sRow.id].push({index:1})
          })
        }
      })
    });
    if (writeBack) {
      cardStore.setValue(detailRes.data);
    }

    // 设置交易类型字段
    if (detailRes.data?.transTypeKeyField) {
      const formStore = rootStore.getStoreByAlias(cardStore.formStore)
      formStore.setTransTypeField(detailRes.data.transTypeKeyField)
    }

    return detailRes;
  } catch (err: any) {
    rootStore.utils.message.danger(err?.message);
    return Promise.reject(err);
  } finally {
    // pageStore?.endLoading?.();
    this?.hideProgress?.()
  }
}

export async function detailPaging (rootStore: any, options: any = {}) {
  requiredValidate.call(this, options, ['cardStoreKey']);
  const { dataSourceAlias = '', cardStoreKey = 'cardStore' } = options;
  const { pageStore = {} } = rootStore;
  const { params = {}, query = {} } = pageStore;
  const cardStore = rootStore.get(cardStoreKey);
  if (!cardStore) throw (new Error(`The CardStore[${cardStoreKey}] not found`));

  const queryData = cardStore.getQueryParams({ alias: cardStoreKey }, true)
  if (!queryData.needQueryPaging) return
  try {
    pageStore?.startLoading?.();
    let queryParams = {
      params: {
        ...query,
        mode: cardStore.uiState || pageStore.status,
        datasource: dataSourceAlias || cardStore?.dataSource || '',
        billnum: options.billNo || params.billNo,
        busiObj: options.busiObj || query.busiObj,
        id: options.billId || params.billId
      },
      data: queryData?.data
    };
    // beforeDetailHook
    if (isType(this.beforeDetailPaginglHook, 'Function')) {
      queryParams = await this.beforeDetailPaginglHook(rootStore, queryParams);
    }
    let pagingInfo = await services.detail(queryParams);
    pagingInfo = pagingInfo?.data
    // afterDetailHook
    if (isType(this.afterDetailPagingHook, 'Function')) {
      pagingInfo = await this.afterDetailPagingHook(rootStore, pagingInfo);
    }
    cardStore.tableStores.forEach(sDescription => {
      const { paging: sPaging, dataSource: sDataSource, alias: sAlias, children } = sDescription
      const sStore = rootStore[sAlias]
      if (children?.length) {
        children.forEach(gDescription => {
          const { alias: gAlias, paging: gPaging, dataSource: gDataSource } = gDescription
          const gPagingStore = rootStore[gPaging]
          if (gPaging) {
            gPagingStore.sActiveChangeReaction?.()
            gPagingStore.sActiveChangeReaction = reaction(
              () => sStore.currentActiveRow?.getValue(ROW_KEY),
              (sActiveRowKey) => {
                const total = pagingInfo?.[sDataSource]?.[sActiveRowKey]?.[gDataSource]?.totalCount || 0
                gPagingStore.setPageInfo({
                  total
                })
              }
            )
          }
        })
      }
      if (!sPaging) return
      if (!sStore.loadedPages) sStore.loadedPages = []
      if (!pagingInfo?.[sDataSource]?.totalCount) return
      let index = sStore.loadedPages?.findIndex((ele: any) => ele?.index === 1)
      index !== -1 ? sStore.loadedPages[index] = { index: 1 } : sStore.loadedPages.push({ index: 1 })
      // sStore.loadedPages.push({index:1})
      const pagingStore = rootStore[sPaging]
      pagingStore.setPageInfo({
        total: pagingInfo?.[sDataSource]?.totalCount
      })
    });
    return pagingInfo;
  } catch (err: any) {
    return Promise.reject(err);
  } finally {
    pageStore?.endLoading?.();
  }
}
